import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRightIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { supabase } from '../clients/supabaseClient';
import { fetchCollectionOrdersFromDb, type CollectionOrder } from '../services/orderService';
import StatusBadge from '../components/StatusBadge';
import { ORDER_STATUS } from '../constants/orderStatuses';
import TablePagination from "../components/TablePagination.tsx";

const ITEMS_PER_PAGE = 25;

const STATUS_OPTIONS = [
    { value: ORDER_STATUS.AWAITING_PACKING,     label: 'Awaiting Packing' },
    { value: ORDER_STATUS.PACKER_ASSIGNED,      label: 'Packer Assigned' },
    { value: ORDER_STATUS.PACKING_COMPLETE,     label: 'Packing Complete' },
    { value: ORDER_STATUS.READY_FOR_COLLECTION, label: 'Ready for Collection' },
    { value: ORDER_STATUS.COLLECTION_COMPLETE,  label: 'Collected' },
    { value: ORDER_STATUS.ORDER_CANCELLED,      label: 'Cancelled' },
];

const Collections: FC = () => {
    const navigate = useNavigate();
    const statusDropdownRef = useRef<HTMLDivElement | null>(null);

    const [orders,               setOrders]               = useState<CollectionOrder[]>([]);
    const [loading,              setLoading]              = useState(true);
    const [activeTab,            setActiveTab]            = useState<'open' | 'collected'>('open');
    const [statusFilters,        setStatusFilters]        = useState<string[]>([]);
    const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
    const [searchQuery,          setSearchQuery]          = useState('');
    const [currentPage,          setCurrentPage]          = useState(1);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await fetchCollectionOrdersFromDb();
            setOrders(data);
        } catch { /* non-fatal — table stays empty */ }
        finally { setLoading(false); }
    }, []);

    useEffect(() => {
        void load();
        const channel = supabase
            .channel('collection-orders-changes')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'orders', filter: 'delivery_type=eq.collection' },
                () => { void load(); })
            .subscribe();
        return () => { void supabase.removeChannel(channel); };
    }, [load]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target as Node)) {
                setIsStatusDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => { setStatusFilters([]); setCurrentPage(1); }, [activeTab]);
    useEffect(() => { setCurrentPage(1); }, [statusFilters, searchQuery]);

    const openOrders = useMemo(() =>
            orders.filter(o => o.status?.toUpperCase() !== ORDER_STATUS.COLLECTION_COMPLETE),
        [orders]);

    // Allowlist, not "everything not open" — a canceled order was never
    // actually collected, so it shouldn't land here by elimination.
    const collectedOrders = useMemo(() =>
            orders.filter(o => o.status?.toUpperCase() === ORDER_STATUS.COLLECTION_COMPLETE),
        [orders]);

    const baseOrders = activeTab === 'collected' ? collectedOrders : openOrders;

    const filteredOrders = useMemo(() => {
        let result = baseOrders;
        if (statusFilters.length > 0) {
            result = result.filter(o => statusFilters.includes(o.status?.toUpperCase()));
        }
        if (searchQuery.trim()) {
            const q = searchQuery.toLowerCase();
            result = result.filter(o =>
                o.reference?.toLowerCase().includes(q) ||
                o.customers?.first_name?.toLowerCase().includes(q) ||
                o.customers?.last_name?.toLowerCase().includes(q) ||
                o.collection_point?.name?.toLowerCase().includes(q)
            );
        }
        return result;
    }, [baseOrders, statusFilters, searchQuery]);

    const totalItems = filteredOrders.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const paginated  = filteredOrders.slice(startIndex, startIndex + ITEMS_PER_PAGE);

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const toggleStatusFilter = (s: string) =>
        setStatusFilters(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);

    const statusFilterLabel = statusFilters.length === 0
        ? 'Filter by status'
        : `${statusFilters.length} filter${statusFilters.length > 1 ? 's' : ''} active`;

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-semibold text-stone-900">Collections</h1>
                <p className="mt-1 text-sm text-stone-500">All orders where the customer collects in store</p>
            </div>

            <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="inline-flex overflow-hidden rounded-lg border border-stone-300 bg-white shadow-sm">
                    <button onClick={() => setActiveTab('open')}
                            className={`px-4 py-2 text-sm font-medium transition-colors ${activeTab === 'open' ? 'bg-stone-900 text-white' : 'text-stone-700 hover:bg-stone-50'}`}>
                        Open
                    </button>
                    <button onClick={() => setActiveTab('collected')}
                            className={`px-4 py-2 text-sm font-medium transition-colors ${activeTab === 'collected' ? 'bg-stone-900 text-white' : 'text-stone-700 hover:bg-stone-50'}`}>
                        Collected
                    </button>
                </div>

                <div className="flex items-center gap-3 flex-wrap">
                    <div className="relative">
                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-stone-400" />
                        <input type="text" placeholder="Search ref, customer, collection point..."
                               value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                               className="w-72 rounded-lg border border-stone-300 bg-white py-2.5 pl-10 pr-4 text-sm shadow-sm placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-[#ee2b74]" />
                    </div>

                    {activeTab === 'open' && (
                        <div ref={statusDropdownRef} className="relative">
                            <button onClick={() => setIsStatusDropdownOpen(p => !p)}
                                    className="flex items-center gap-2 rounded-lg border border-stone-300 bg-white px-4 py-2.5 text-sm text-stone-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#ee2b74]">
                                <span>{statusFilterLabel}</span>
                                <svg className="h-4 w-4 text-stone-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                            {isStatusDropdownOpen && (
                                <div className="absolute right-0 z-20 mt-2 w-56 rounded-xl border border-stone-200 bg-white shadow-lg">
                                    <div className="flex items-center justify-between border-b border-stone-100 px-3 py-2">
                                        <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">Status</span>
                                        <button onClick={() => setStatusFilters([])}
                                                className="text-xs font-medium text-[#ee2b74] hover:underline">Clear</button>
                                    </div>
                                    <div className="py-2">
                                        {STATUS_OPTIONS.map(opt => (
                                            <label key={opt.value} className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm text-stone-700 hover:bg-stone-50">
                                                <input type="checkbox" checked={statusFilters.includes(opt.value)}
                                                       onChange={() => toggleStatusFilter(opt.value)}
                                                       className="h-4 w-4 rounded border-stone-300 accent-[#ee2b74]" />
                                                {opt.label}
                                            </label>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="border-b border-stone-200 bg-stone-50">
                        <tr>
                            {['Order Ref', 'Customer', 'Collection Point', 'Status', 'Date', ''].map(h => (
                                <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-stone-500">{h}</th>
                            ))}
                        </tr>
                        </thead>
                        <tbody className="divide-y divide-stone-100">
                        {loading ? (
                            Array.from({ length: 8 }).map((_, i) => (
                                <tr key={i} className="animate-pulse">
                                    {Array.from({ length: 6 }).map((_, j) => (
                                        <td key={j} className="px-4 py-4"><div className="h-4 rounded bg-stone-100" /></td>
                                    ))}
                                </tr>
                            ))
                        ) : paginated.length === 0 ? (
                            <tr>
                                <td colSpan={6} className="px-4 py-10 text-center text-sm text-stone-500">
                                    {activeTab === 'collected' ? 'No collected orders found.' : 'No open collection orders.'}
                                </td>
                            </tr>
                        ) : paginated.map(order => (
                            <tr key={order.id} onClick={() => navigate(`/orders/${order.id}`)}
                                className="group cursor-pointer transition hover:bg-stone-50">
                                <td className="px-4 py-3 font-medium text-stone-900">{order.reference ?? '—'}</td>
                                <td className="px-4 py-3 text-stone-700">
                                    {order.customers ? `${order.customers.first_name} ${order.customers.last_name}` : '—'}
                                </td>
                                <td className="px-4 py-3 text-stone-700">
                                    {order.collection_point ? (
                                        <div>
                                            <p className="font-medium text-stone-900">{order.collection_point.name}</p>
                                            <p className="text-xs text-stone-400">{order.collection_point.address_line1}</p>
                                        </div>
                                    ) : (
                                        <span className="text-stone-400 italic text-xs">No point selected</span>
                                    )}
                                </td>
                                <td className="px-4 py-3"><StatusBadge status={order.status} /></td>
                                <td className="px-4 py-3 text-stone-500 text-xs">
                                    {new Date(order.created_at).toLocaleDateString('en-ZA', {
                                        day: 'numeric', month: 'short', year: 'numeric',
                                    })}
                                </td>
                                <td className="px-4 py-3">
                                    <ChevronRightIcon className="h-5 w-5 text-stone-300 group-hover:text-stone-500 transition" />
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>

                {!loading && totalItems > 0 && (
                    <TablePagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalItems={totalItems}
                        itemsPerPage={ITEMS_PER_PAGE}
                        onPageChange={handlePageChange}
                    />
                )}
            </div>
        </div>
    );
};

export default Collections;